/**
 * @fileoverview Component for creating a new participant.
 * This component includes a form for creating a new participant and a button for confirming the creation.
 */

import { XMarkIcon } from '@heroicons/react/24/outline'
import { AxiosResponse } from 'axios'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import QuestionIcon from '../../../assets/question.svg'
import EmailIcon from '../../../assets/share/email.svg'
import SiteIcon from '../../../assets/site.svg'
import StudyIcon from '../../../assets/study.svg'
import { useModal } from '../../../hooks/useModal'
import { useToast } from '../../../hooks/useToast'
import { useCreateParticipantMutation } from '../../../services/participants'
import { useGetSitesQuery } from '../../../services/sites'
import { useGetStudiesQuery } from '../../../services/studies'
import Dropdown from '../../Dropdown'
import { PrimaryButton } from '../../elements/PrimaryButton'
import { RegularButton } from '../../elements/RegularButton'
import { PhoneInputComponent } from '../../PhoneInput'

/**
 * @interface Payload
 * @description The payload for creating a new participant.
 * @property {string} study_id - The ID of the study.
 * @property {string} external_id - The external ID of the participant.
 * @property {string} email - The email address of the participant.
 * @property {string} site_id - The ID of the site.
 * @property {string} language - The language of the participant.
 * @property {string} name - The name of the participant.
 * @property {string} phone - The phone number of the participant.
 */
interface Payload {
    study_id: string
    external_id: string
    email?: string
    site_id: string
    language: string
    name?: string
    phone?: string
}

/**
 * @interface ValidationError
 * @description The error response from the API.
 * @property {number} code - The error code.
 * @property {Record<string, string>} data - The error data.
 * @property {string} message - The error message.
 */
interface ValidationError {
    code: number
    data: Record<string, string>
    message: string
}

/**
 * @function classNames
 * @description Helper function to concatenate class names.
 * @param {...string} classes - The class names to concatenate.
 * @returns {string} - The concatenated class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function formatSiteOptions
 * @description Enhances the default list of sites with the additional "Match to nearest site automatically" option
 * @param {Novoic.API.Site[]} sites - The list of sites.
 * @returns {Array<{title: string, value: string, icon: JSX.Element}>} - The enhanced list of sites.
 */
function formatSiteOptions(sites: Novoic.API.Site[] = []) {
    return sites.map((site) => ({
        title: site.name,
        value: site.id,
        icon: <SiteIcon className="text-indigo-500" />,
    }))
}

/**
 * @function NewParticipant
 * @description Component for creating a new participant.
 * @returns {JSX.Element} - A React component that renders the form for creating a new participant.
 */
export function NewParticipant() {
    const [, close] = useModal()
    const navigate = useNavigate()
    const [openToast] = useToast()

    const { data: studies, isLoading: areStudiesLoading } = useGetStudiesQuery()
    const { data: sites } = useGetSitesQuery()
    const [createParticipant] = useCreateParticipantMutation()
    const [requiredFields, setRequiredFields] = useState<Set<string>>(new Set([]))

    const [form, updateForm] = useState<Partial<Payload>>({
        external_id: '',
        study_id: '',
        site_id: null,
        email: '',
        phone: '',
    })
    const [countryCode, setCountryCode] = useState<'US' | 'GB'>()

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

    useEffect(() => {
        if (!sites || !studies) return
        setCountryCode(studies[0].jurisdiction)
        updateForm({ ...form, study_id: studies[0].id, site_id: sites[0].id })
    }, [sites, studies])

    function onChange(name: string, value: unknown) {
        updateForm({ ...form, [name]: value })
        setValidationErrors({ ...validationErrors, [name]: undefined })
    }

    function getCTACopy() {
        if (requiredFields.has('phone') || requiredFields.has('email')) {
            return 'Create & Send Link'
        }

        return 'Create Participant'
    }

    function isValidForm(requiredFields: Set<string>) {
        const required = [
            requiredFields.has('phone') ? 'phone' : undefined,
            requiredFields.has('email') ? 'email' : undefined,
            'study_id',
        ] as (keyof Payload)[]
        let valid = true
        const newValidationErrors = { ...validationErrors }

        if (form.email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            setValidationErrors({
                ...validationErrors,
                email: "This doesn't look like a valid email.",
            })
            return
        }

        required
            .filter((x) => x)
            .forEach((field) => {
                if (!form[field] || form[field].length === 0) {
                    if (field === 'phone') {
                        newValidationErrors[field] =
                            'Provide a phone number where the invitation to the test will be sent.'
                    } else if (field === 'email') {
                        newValidationErrors[field] =
                            'Provide an email address where the invitation to the test will be sent.'
                    }
                    valid = false
                }
            })

        setValidationErrors(newValidationErrors)
        return valid
    }

    async function submit(form: Partial<Payload>) {
        if (!isValidForm(requiredFields)) return

        const data: Record<string, unknown> = {}
        Object.entries(form).forEach(([key, value]) => {
            if (!value) {
                return
            }
            if (key === 'phone') {
                value = (value as string).replace(/\s/g, '')
            }
            data[key] = value
        })

        if (requiredFields.has('phone')) {
            data['notify_by_phone'] = true
        }

        if (requiredFields.has('email')) {
            data['notify_by_email'] = true
        }

        try {
            const participant = await createParticipant(data).unwrap()

            if (requiredFields.has('phone') || requiredFields.has('email')) {
                openToast({
                    title: 'Invitation sent!',
                    icon: <EmailIcon className="h-5 w-5 text-indigo-500" aria-hidden="true" />,
                })
            }

            close()
            navigate(`/app/participants/all/${participant.id}`)
        } catch (error: unknown) {
            const { data: response } = error as AxiosResponse<ValidationError>
            const acc: Record<string, string> = {}

            Object.entries(response.data).forEach(([key]) => {
                if (key === 'phone') {
                    acc[key] = 'This phone number already exists.'
                }

                if (key === 'email') {
                    acc[key] = 'This email already exists.'
                }
            })

            setValidationErrors(acc)
        }
    }

    return (
        <>
            <div className="flex justify-between items-center gap-4">
                <h2 className="block font-semibold text-xl text-gray-900">New Participant</h2>
                <button className="w-6 h-6" onClick={close}>
                    <XMarkIcon />
                </button>
            </div>
            <div className="gap-x-4 w-[26rem]">
                <div className="grid grid-cols-2 gap-x-4 gap-y-6 mb-8">
                    <div>
                        <div className="mb-1">
                            <label
                                htmlFor="name"
                                className="block text-sm font-medium text-gray-700"
                            >
                                <span>Full name</span>&nbsp;
                                <span className="font-normal text-gray-500">(optional)</span>
                            </label>
                        </div>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            placeholder={'Participant’s full name'}
                            onChange={(e) => {
                                onChange('name', e.target.value)
                            }}
                            className="h-10 w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                        />
                    </div>

                    <div>
                        <div className="mb-1">
                            <label
                                htmlFor="external_id"
                                className="flex text-sm font-medium text-gray-700"
                            >
                                <span>External ID</span>&nbsp;
                                <span className="font-normal text-gray-500"> (optional)</span>
                                <div className="relative flex justify-end grow">
                                    <QuestionIcon
                                        id="external-id-tooltip"
                                        className="w-5 h-5 fill-gray-400 transition-colors cursor-pointer hover:fill-indigo-500"
                                    />
                                </div>
                            </label>
                        </div>
                        <input
                            type="text"
                            id="external_id"
                            name="external_id"
                            required
                            placeholder="External ID"
                            onChange={(e) => {
                                onChange('external_id', e.target.value)
                            }}
                            className={`h-10 w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md ${
                                validationErrors.external_id ? 'border-red-300' : ''
                            }`}
                        />
                    </div>

                    <div className="flex flex-col col-span-2">
                        <Dropdown
                            label="Choose a study to include participant in"
                            value={form.study_id}
                            name="study_id"
                            onChange={(value: string) => {
                                updateForm({ ...form, study_id: value, phone: '' })
                                setCountryCode(
                                    studies?.find((study) => study.id === value)?.jurisdiction as
                                        | 'US'
                                        | 'GB'
                                )
                            }}
                            placeholder="Choose a study"
                            hint="The study determines the specific test battery and the sites to which a participant might be matched."
                            disabled={areStudiesLoading || studies?.length === 1}
                            options={studies?.map((study) => ({
                                title: study.name,
                                value: study.id,
                                icon: <StudyIcon />,
                            }))}
                            error={validationErrors.study_id}
                        />
                    </div>
                    <div className="flex flex-col col-span-2">
                        <Dropdown
                            name="site_id"
                            label="Choose a site to match participant to"
                            value={form.site_id}
                            onChange={(value) => {
                                onChange('site_id', value)
                            }}
                            placeholder="Choose a site"
                            hint={`The participant will be visible only to the staff of the chosen site. If "Match to nearest site automatically" is selected, the participant will be automatically matched to one of the active sites within the chosen study based on their location.`}
                            disabled={sites?.length === 1}
                            options={formatSiteOptions(sites)}
                            error={validationErrors.site_id}
                        />
                    </div>

                    <div className="flex flex-col col-span-2">
                        <label
                            htmlFor="phone"
                            className="flex text-sm font-medium text-gray-700 mb-1"
                        >
                            Participant or carer phone number&nbsp;
                            {!requiredFields.has('phone') && (
                                <span className="font-normal text-gray-500">(optional)</span>
                            )}
                            <div className="relative flex justify-end grow">
                                <QuestionIcon
                                    id={`phone-tooltip`}
                                    className="w-5 h-5 fill-gray-400 transition-colors cursor-pointer hover:fill-indigo-500"
                                />
                            </div>
                        </label>
                        <PhoneInputComponent
                            id={'phone'}
                            countryCode={countryCode}
                            value={form.phone}
                            onChange={(value) => onChange('phone', value)}
                            error={validationErrors.phone}
                        />
                    </div>

                    <div className="flex flex-col col-span-2">
                        <label
                            htmlFor="email"
                            className="flex text-sm font-medium text-gray-700 mb-1"
                        >
                            Participant or carer email address&nbsp;
                            {!requiredFields.has('email') && (
                                <span className="font-normal text-gray-500">(optional)</span>
                            )}
                            <div className="relative flex justify-end grow">
                                <QuestionIcon
                                    id={`email-tooltip`}
                                    className="w-5 h-5 fill-gray-400 transition-colors cursor-pointer hover:fill-indigo-500"
                                />
                            </div>
                        </label>
                        <input
                            type={'email'}
                            id={'email'}
                            placeholder={'Provide email address'}
                            onChange={(e) => {
                                onChange('email', e.target.value)
                            }}
                            name={'email'}
                            className={`h-10 w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md ${
                                validationErrors.email ? 'border-red-300' : ''
                            }`}
                        />
                        {validationErrors.email && (
                            <p className="pt-1 text-xs text-red-600">{validationErrors.email}</p>
                        )}
                    </div>

                    <div className="flex flex-col col-span-2">
                        <div className="mb-1">
                            <label
                                htmlFor="external_id"
                                className="flex text-sm font-medium text-gray-700"
                            >
                                <span>Choose how to send the test link</span>&nbsp;
                                <span className="font-normal text-gray-500">(optional)</span>
                                <div className="relative flex justify-end grow">
                                    <QuestionIcon
                                        id={`share-tooltip`}
                                        className="w-5 h-5 fill-gray-400 transition-colors cursor-pointer hover:fill-indigo-500"
                                    />
                                </div>
                            </label>
                        </div>

                        <div className="flex">
                            <label
                                id="sms-link"
                                className={classNames(
                                    'flex grow border border-gray-300 rounded-l-md border-r-0 p-4 basis-0 items-center',
                                    requiredFields.has('phone') &&
                                        'bg-indigo-50 bg-opacity-50 border-indigo-200'
                                )}
                            >
                                <input
                                    type="checkbox"
                                    checked={requiredFields.has('phone')}
                                    className="focus:ring-indigo-500 h-4 w-4 disabled:bg-gray-200 text-indigo-600 border-gray-300 rounded"
                                    onChange={(e) => {
                                        setValidationErrors({
                                            ...validationErrors,
                                            phone: undefined,
                                        })
                                        if (e.target.checked) {
                                            setRequiredFields(new Set(requiredFields.add('phone')))
                                        } else {
                                            requiredFields.delete('phone')
                                            setRequiredFields(new Set(requiredFields))
                                        }
                                    }}
                                />
                                <span className="ml-3 block text-sm font-medium text-gray-700">
                                    Text message (SMS)
                                </span>
                            </label>
                            <label
                                className={classNames(
                                    'flex grow border border-gray-300 rounded-r-md p-4 basis-0 items-center',
                                    requiredFields.has('email') &&
                                        'bg-indigo-50 bg-opacity-50 border-indigo-200'
                                )}
                            >
                                <input
                                    type="checkbox"
                                    checked={requiredFields.has('email')}
                                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    onChange={(e) => {
                                        setValidationErrors({
                                            ...validationErrors,
                                            email: undefined,
                                        })
                                        if (e.target.checked) {
                                            setRequiredFields(new Set(requiredFields.add('email')))
                                        } else {
                                            requiredFields.delete('email')
                                            setRequiredFields(new Set(requiredFields))
                                        }
                                    }}
                                />
                                <span className="ml-3 block text-sm font-medium text-gray-700">
                                    Email
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="flex col-span-2 justify-end gap-2">
                    <RegularButton onClick={() => close()}>Cancel</RegularButton>
                    <PrimaryButton onClick={() => submit(form)}>{getCTACopy()}</PrimaryButton>
                </div>

                <Tooltip anchorSelect="#external-id-tooltip">
                    <p className="w-40 font-normal text-xs">
                        You can associate the participant&apos;s record on the Novoic Dashboard with
                        the one in the external system by providing the participant&apos;s ID from
                        that system.
                    </p>
                </Tooltip>

                <Tooltip anchorSelect={`#phone-tooltip`}>
                    <p className="w-40 font-normal text-xs">
                        By providing a participant or carer&apos;s phone number, you can send
                        automated text invitations for test completion. This also improves test
                        completion rates by eliminating the need for participants to provide and
                        verify their phone numbers. The phone number will be virtualized and masked
                        to ensure privacy.
                    </p>
                </Tooltip>

                <Tooltip anchorSelect={`#email-tooltip`}>
                    <p className="w-40 font-normal text-xs">
                        By providing a participant or carer&apos;s email address, you can send
                        automated invitations for test completion, engaging reminders to finish the
                        test, and additional email updates to manage expectations. This also
                        improves test completion rates by eliminating the need for participants to
                        provide their emails.
                    </p>
                </Tooltip>

                <Tooltip anchorSelect={`#share-tooltip`}>
                    <p className="w-40 font-normal text-xs">
                        The participant will immediately receive an automated invitation to complete
                        a test at the provided email and/or phone number.
                    </p>
                </Tooltip>
            </div>
        </>
    )
}
